import { useEffect, useState } from 'react'
import { jsPDF } from 'jspdf'
import { SendQuotePanel } from './SendQuotePanel'
import './AdminDashboard.sales.css'

const STATUS_OPTIONS = [
  { value: 'quote', label: { en: 'Quote', fr: 'Devis' } },
  { value: 'accepted', label: { en: 'Accepted', fr: 'Accept\u00e9' } },
  { value: 'invoice', label: { en: 'Invoice', fr: 'Facture' } },
  { value: 'confirmed', label: { en: 'Confirmed', fr: 'Confirm\u00e9e' } },
  { value: 'delivered', label: { en: 'Delivered', fr: 'Livr\u00e9e' } },
  { value: 'paid', label: { en: 'Paid', fr: 'Pay\u00e9e' } },
  { value: 'cancelled', label: { en: 'Cancelled', fr: 'Annul\u00e9e' } },
]

const L = {
  en: {
    title: 'Sale details', reference: 'Reference', client: 'Client', product: 'Product / Service',
    amount: 'Amount', status: 'Status', date: 'Date', notes: 'Notes', invoiceUrl: 'Invoice URL',
    openInvoice: 'Open invoice', downloadPdf: 'Download PDF', edit: 'Edit', save: 'Save changes',
    cancel: 'Cancel', close: 'Close', remove: 'Delete',
    noInvoice: 'No hosted invoice URL. Paste one below or download a generated PDF.',
    saved: 'Saved.', error: 'Error', sendQuote: 'Send Quote',
    sendSection: 'Send quote / invoice by email', toEmail: 'To', replyTo: 'Reply-To',
    subject: 'Subject', body: 'Message', sending: 'Sending\u2026', sentOk: 'Quote email sent.',
    invalidEmail: 'Enter a valid email address.',
  },
  fr: {
    title: 'D\u00e9tail de la vente', reference: 'R\u00e9f\u00e9rence', client: 'Client',
    product: 'Produit / Service', amount: 'Montant', status: 'Statut', date: 'Date', notes: 'Notes',
    invoiceUrl: 'URL de la facture', openInvoice: 'Ouvrir la facture',
    downloadPdf: 'T\u00e9l\u00e9charger le PDF', edit: 'Modifier', save: 'Enregistrer',
    cancel: 'Annuler', close: 'Fermer', remove: 'Supprimer',
    noInvoice: 'Aucune URL de facture. Collez un lien ou t\u00e9l\u00e9chargez un PDF g\u00e9n\u00e9r\u00e9.',
    saved: 'Enregistr\u00e9.', error: 'Erreur', sendQuote: 'Envoyer le devis',
    sendSection: 'Envoyer le devis / la facture par e-mail', toEmail: 'Destinataire',
    replyTo: 'R\u00e9pondre \u00e0', subject: 'Objet', body: 'Message', sending: 'Envoi\u2026',
    sentOk: 'E-mail du devis envoy\u00e9.', invalidEmail: 'Saisissez une adresse e-mail valide.',
  },
}

function invoiceOf(sale) {
  return (sale?.invoiceUrl || sale?.invoice_url || '').trim()
}

function toDateInput(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10)
  return d.toISOString().slice(0, 10)
}

function buildPdfDoc(sale, lang) {
  const t = L[lang] || L.fr
  const doc = new jsPDF()
  const ref = sale.reference || `SALE-${sale.id || 'X'}`
  const dash = '\u2014'
  doc.setFontSize(16)
  doc.text('Groupe Ouambo', 14, 20)
  doc.setFontSize(12)
  doc.text(lang === 'fr' ? 'Devis / Facture' : 'Quote / Invoice', 14, 28)
  doc.setFontSize(11)
  const rows = [
    [t.reference, ref],
    [t.client, sale.client || dash],
    [t.product, sale.product || dash],
    [t.amount, String(sale.amount ?? dash)],
    [t.status, sale.status || dash],
    [t.date, sale.date ? new Date(sale.date).toLocaleDateString() : dash],
    [t.notes, sale.notes || sale.description || dash],
  ]
  let y = 40
  rows.forEach(([k, v]) => {
    doc.setFont(undefined, 'bold')
    doc.text(`${k}:`, 14, y)
    doc.setFont(undefined, 'normal')
    const lines = doc.splitTextToSize(String(v), 120)
    doc.text(lines, 55, y)
    y += 8 + (lines.length - 1) * 6
  })
  const safe = String(ref).replace(/[^\w.-]+/g, '_')
  const prefix = (sale.status === 'quote' || sale.status === 'pending' || sale.status === 'accepted') ? 'Quote' : 'INV'
  return { doc, filename: `${prefix}-${safe}.pdf` }
}

function buildPdf(sale, lang) {
  const { doc, filename } = buildPdfDoc(sale, lang)
  const dataUri = doc.output('datauristring')
  return { filename, base64: dataUri.substring(dataUri.indexOf(',') + 1) }
}

function downloadPdf(sale, lang) {
  const { doc, filename } = buildPdfDoc(sale, lang)
  doc.save(filename)
}

export default function SaleDetailModal({ sale, lang = 'fr', onClose, onSave, onDelete, onStatusChange }) {
  const t = L[lang] || L.fr
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [form, setForm] = useState({
    client: '', product: '', amount: '', status: 'quote', date: '', notes: '', reference: '', invoiceUrl: '',
  })

  useEffect(() => {
    if (!sale) return
    setEditing(false)
    setMsg('')
    setForm({
      client: sale.client || '',
      product: sale.product || '',
      amount: sale.amount != null ? String(sale.amount) : '',
      status: sale.status || 'quote',
      date: toDateInput(sale.date),
      notes: sale.notes || sale.description || '',
      reference: sale.reference || '',
      invoiceUrl: invoiceOf(sale),
    })
  }, [sale])

  if (!sale) return null

  const set = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }))
  const dash = '\u2014'

  const handleSave = async () => {
    setBusy(true)
    setMsg('')
    try {
      await onSave({
        client: form.client.trim(),
        product: form.product.trim(),
        amount: form.amount.trim(),
        status: form.status,
        date: form.date || null,
        notes: form.notes.trim() || null,
        reference: form.reference.trim() || null,
        invoiceUrl: form.invoiceUrl.trim() || null,
      })
      setEditing(false)
      setMsg(t.saved)
    } catch (err) {
      setMsg(`${t.error}: ${err.message}`)
    } finally {
      setBusy(false)
    }
  }

  const handleDownload = () => {
    const merged = { ...sale, ...form }
    const url = invoiceOf(editing ? { ...merged, invoiceUrl: form.invoiceUrl } : sale)
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
      return
    }
    downloadPdf(merged, lang)
  }

  const inv = invoiceOf(editing ? { invoiceUrl: form.invoiceUrl } : sale)

  return (
    <div className="dash-modal-overlay" onClick={onClose}>
      <div className="dash-modal dash-sale-modal" onClick={(e) => e.stopPropagation()}>
        <div className="dash-modal-header">
          <h3>{t.title}</h3>
          <button type="button" className="dash-modal-close" onClick={onClose}>{'\u2715'}</button>
        </div>

        {!editing ? (
          <div className="dash-detail-card" style={{ boxShadow: 'none', border: 'none', padding: 0 }}>
            <div className="dash-detail-row"><span>{t.reference}</span><strong className="dash-ref">{sale.reference || dash}</strong></div>
            <div className="dash-detail-row"><span>{t.client}</span><span>{sale.client || dash}</span></div>
            <div className="dash-detail-row"><span>{t.product}</span><span>{sale.product || dash}</span></div>
            <div className="dash-detail-row"><span>{t.amount}</span><strong>{sale.amount}</strong></div>
            <div className="dash-detail-row"><span>{t.status}</span>
              <select className="dash-status-select-sm" value={sale.status || 'quote'} onChange={(e) => onStatusChange?.(sale.id, e.target.value)}>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label[lang] || o.label.en}</option>
                ))}
              </select>
            </div>
            <div className="dash-detail-row"><span>{t.date}</span><span>{sale.date ? new Date(sale.date).toLocaleDateString() : dash}</span></div>
            <div className="dash-detail-row"><span>{t.notes}</span><span>{sale.notes || sale.description || dash}</span></div>
            <div className="dash-detail-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
              <span>{t.invoiceUrl}</span>
              {inv ? (
                <a className="dash-btn primary small" href={inv} target="_blank" rel="noopener noreferrer" style={{ width: 'fit-content', textDecoration: 'none' }}>
                  {'\uD83D\uDCC4'} {t.openInvoice}
                </a>
              ) : (
                <p className="dash-muted" style={{ margin: 0 }}>{t.noInvoice}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="dash-form" style={{ gap: 10 }}>
            <label>{t.reference}<input value={form.reference} onChange={set('reference')} /></label>
            <label>{t.client}<input value={form.client} onChange={set('client')} required /></label>
            <label>{t.product}<input value={form.product} onChange={set('product')} required /></label>
            <label>{t.amount}<input value={form.amount} onChange={set('amount')} required /></label>
            <label>{t.status}
              <select value={form.status} onChange={set('status')}>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label[lang] || o.label.en}</option>
                ))}
              </select>
            </label>
            <label>{t.date}<input type="date" value={form.date} onChange={set('date')} /></label>
            <label>{t.notes}<textarea rows={3} value={form.notes} onChange={set('notes')} /></label>
            <label>{t.invoiceUrl}<input type="url" value={form.invoiceUrl} onChange={set('invoiceUrl')} placeholder="https://..." /></label>
          </div>
        )}

        <SendQuotePanel sale={sale} form={form} lang={lang} t={t} buildPdf={buildPdf} invoiceOf={invoiceOf} busy={busy} />

        {msg && <p className="dash-status-msg" style={{ marginTop: 10 }}>{msg}</p>}

        <div className="dash-form-actions" style={{ marginTop: 16, flexWrap: 'wrap', gap: 8 }}>
          {!editing ? (
            <>
              <button type="button" className="dash-btn secondary" onClick={onClose}>{t.close}</button>
              <button type="button" className="dash-btn secondary" onClick={() => setEditing(true)}>{t.edit}</button>
              <button type="button" className="dash-btn primary" onClick={handleDownload}>{'\uD83D\uDCE5'} {t.downloadPdf}</button>
              <button type="button" className="dash-delete-btn" onClick={async () => { await onDelete?.(sale.id); onClose?.() }}>{t.remove}</button>
            </>
          ) : (
            <>
              <button type="button" className="dash-btn secondary" disabled={busy} onClick={() => setEditing(false)}>{t.cancel}</button>
              <button type="button" className="dash-btn primary" disabled={busy} onClick={handleSave}>{t.save}</button>
              <button type="button" className="dash-btn secondary" onClick={handleDownload}>{'\uD83D\uDCE5'} {t.downloadPdf}</button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
